import { inject, Injectable, InjectionToken } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { ObjectState } from '@meddev/fe-shared';
import { AuthAppService, AuthStateEnum } from '@shared/data';
import { filter, first, map, Observable, of, switchMap, tap } from 'rxjs';
import { BaseInitAppService } from '../service/base-init-app.service';

export const GUARD_CONFIG = new InjectionToken<AuthGuardConfig>('GUARD_CONFIG');

export interface AuthGuardConfig {
    loginRoute: string;
    redirectAfterLoginParameter: string;
}

@Injectable({
    providedIn: 'root',
})
export class AuthGuard {
    private readonly authService = inject(AuthAppService);
    private readonly router = inject(Router);
    private readonly config = inject(GUARD_CONFIG);

    canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.authService.authState$.pipe(
            first(),
            map(authInfo => {
                const hasAccess = authInfo.state === ObjectState.VALID && authInfo.value === AuthStateEnum.HAS_ACCESS;
                return hasAccess;
            }),
            tap(hasAccess => {
                if (!hasAccess) {
                    const queryParams = {
                        [this.config.redirectAfterLoginParameter]: state.url,
                    };
                    this.router.navigate([this.config.loginRoute], { queryParams });
                }
            }),
        );
    }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const NoAuthGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> => {
    const authAppService = inject(AuthAppService);
    return authAppService.authState$.pipe(
        first(),
        map(authInfo => {
            const hasAccess = authInfo.state === ObjectState.VALID && authInfo.value === AuthStateEnum.HAS_ACCESS;
            return !hasAccess;
        }),
    );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const NoAuthRegistrationGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> => {
    const authAppService = inject(AuthAppService);
    const initAppService = inject(BaseInitAppService);
    return authAppService.authState$.pipe(
        first(),
        switchMap(authInfo => {
            if (authInfo.value !== AuthStateEnum.REGISTRATION) {
                return initAppService.navigateToAppInitializerError().pipe(map(() => true));
            }
            return of(true);
        }),
    );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const AppInitializerErrorGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> => {
    const initAppService = inject(BaseInitAppService);
    return initAppService.isInitialized$.pipe(
        filter(initialized => initialized),
        first(),
        switchMap(() => initAppService.appInitError$.pipe(first())),
        switchMap(appInitError => {
            if (appInitError) {
                return initAppService.navigateToAppInitializerError().pipe(map(() => true));
            }
            return of(true);
        }),
    );
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const AppInitializerNoErrorGuard: CanActivateFn = (next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> => {
    const initAppService = inject(BaseInitAppService);
    return initAppService.appInitError$.pipe(
        first(),
        switchMap(appInitError => {
            if (!appInitError) {
                return initAppService.navigateHome().pipe(map(() => true));
            }
            return of(true);
        }),
    );
};
